import {Injectable, Injector} from '@angular/core';
import {UNLEASH_TOGGLES} from '@app/config/FeatureToggleConfig';
import {FeatureTogglesService} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Language, LanguageService, SiteContextConfig, StateWithSiteContext} from '@spartacus/core';
import {Store} from '@ngrx/store';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

@UntilDestroy()
@Injectable()
export class DcpLanguageService extends LanguageService {
  private static readonly DEFAULT_LANGUAGE = 'en';

  private englishOnly = false;

  constructor(
    store: Store<StateWithSiteContext>,
    config: SiteContextConfig,
    private readonly injector: Injector
  ) {
    super(store, config);

    // Defer to a microtask so the eager bootstrap injection graph settles before we resolve
    // FeatureTogglesService, avoiding the LanguageService <-> AuthService circular dependency.
    Promise.resolve().then(() => this.subscribeEnglishOnlyFT());
  }

  private subscribeEnglishOnlyFT(): void {
    const featureTogglesService = this.injector.get(FeatureTogglesService);

    featureTogglesService
      .isEnabled(UNLEASH_TOGGLES.englishOnly)
      .pipe(untilDestroyed(this))
      .subscribe((enabled) => {
        this.englishOnly = enabled;
        if (enabled) {
          this.setActive(DcpLanguageService.DEFAULT_LANGUAGE);
        }
      });
  }

  override getAll(): Observable<Language[]> {
    return super.getAll().pipe(
      map((languages) => (this.englishOnly ? this.filterToDefault(languages) : languages))
    );
  }

  override setActive(isocode: string): void {
    const target = this.englishOnly ? DcpLanguageService.DEFAULT_LANGUAGE : isocode;
    super.setActive(target);
  }

  private filterToDefault(languages: Language[]): Language[] {
    const defaultLanguage = languages.find((language) => language.isocode === DcpLanguageService.DEFAULT_LANGUAGE);
    return defaultLanguage ? [defaultLanguage] : [];
  }
}
